import { useEffect, useRef } from 'react'
import { Avatar, Box, Tooltip } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useLessonCall, type CallParticipantView } from '@/webrtc/callContext'

/**
 * Аватар участника звонка с индикацией голоса: кольцо вокруг аватара растёт с
 * громкостью, поэтому видно, кто сейчас говорит, даже когда все замьючены,
 * кроме одного.
 *
 * Уровень приходит подпиской и пишется прямо в CSS-переменную узла: значение
 * меняется ~15 раз в секунду, и ни ререндер, ни `sx` тут не годятся — `sx`
 * порождал бы новый emotion-класс на каждое значение.
 */
export function CallParticipantAvatar({ participant }: { participant: CallParticipantView }) {
  const { t } = useTranslation()
  const { subscribeVoiceLevels } = useLessonCall()
  const ringRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    return subscribeVoiceLevels((levels) => {
      const node = ringRef.current
      if (!node) return

      node.style.setProperty('--call-level', String(levels[participant.userId] ?? 0))
    })
  }, [participant.userId, subscribeVoiceLevels])

  const initials = participant.name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')

  const isConnecting =
    participant.connectionState !== 'local' && participant.connectionState !== 'connected'

  const tooltip = isConnecting
    ? `${participant.name} — ${t('lessonCall.status.connecting')}`
    : participant.name

  return (
    <Tooltip title={tooltip}>
      <Box
        sx={{
          position: 'relative',
          width: 52,
          height: 52,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          ref={ringRef}
          style={{ '--call-level': 0 } as React.CSSProperties}
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            border: '2px solid',
            borderColor: 'success.main',
            opacity: 'var(--call-level)',
            transform: 'scale(calc(1 + var(--call-level) * 0.22))',
            transition: 'transform 90ms linear, opacity 90ms linear',
            pointerEvents: 'none',
          }}
        />
        <Avatar
          sx={{
            width: 44,
            height: 44,
            bgcolor: isConnecting ? 'action.disabledBackground' : 'primary.main',
            fontSize: 16,
          }}
        >
          {initials}
        </Avatar>
      </Box>
    </Tooltip>
  )
}
